/**
 * Per-field decline escape valve (s98; two-button affordance + follow-up, s179 rebuild).
 *
 * A small secondary control rendered beneath a REQUIRED (starred) field. It never competes with
 * the input:
 *   - eligible + empty + editable → TWO BUTTONS, both answers visible at once
 *   - declined                    → the per-reason status line + a per-reason undo
 *   - filled, or not required, or read-only with no decline → nothing
 *
 * 🔴 WHERE EACH OF THOSE ACTUALLY RENDERS — and a correction, because the previous version of this
 * comment got it wrong in a way worth naming (s179 G4, question 1).
 *
 * This component is mounted at two densities, but the two densities do NOT both offer the entry
 * point. `ExpandableSectionLayout`'s ribbon mount is gated on `isCellDeclined(f.id, rowId)`, so on
 * a COLLAPSED row it renders only the DECLINED state — the status line and its always-visible undo,
 * which is the s178 F3 branch that made a declined cell leaveable from the surface showing it. An
 * empty eligible field on a collapsed row renders no decline affordance at all; it falls through to
 * the ordinary editable cell, and the TWO BUTTONS appear only once the drawer is open.
 *
 * That gate is inherited, not chosen here: it dates to `c198b84c` (2026-06-22), the original decline
 * UI, and the s179 rebuild did not touch it. The buttons simply landed where the old trigger link
 * had always rendered.
 *
 * 🔴 WHY THE OLD COMMENT WAS WRONG, not merely imprecise. It claimed the picker was "a popover, in
 * both densities, deliberately," reasoning that the compact mount is a truncating `text-xs` slot
 * with no room to expand in flow. Every word of that was about a path that CANNOT BE REACHED — the
 * entry point has never rendered at compact density, so there was no in-flow expansion to avoid and
 * no decision to defend. It read as a considered trade-off while describing nothing, which is the
 * same failure shape as the two guards retired tonight (the lens-gate arity proxy and the
 * trigger-label check): an artefact that looks like reasoning but measures an empty set. The next
 * reader would have inherited a rationale for a constraint that does not exist, and might have
 * preserved the popover to honour it.
 *
 * The product consequence is LOGGED, NOT FIXED (Reade's ruling, s179): on the six ungrouped basics
 * a planner can TYPE an answer from the ribbon in one click but must open the drawer to DECLINE
 * one. That asymmetry pre-dates this build and is recorded against PLANNER-MAGIC-1, where the
 * interview becomes the front door — see `deferred-builds.md`.
 *
 * 🔴 WHY TWO BUTTONS AND NOT A PICKER (Reade's ruling, s179 rebuild). The first cut was a neutral
 * trigger link that opened a three-option popover. Two things were wrong with it, and only one was
 * copy. First, the trigger had to say SOMETHING before the planner knew what was on offer, and
 * every honest candidate either asserted one of the answers ("I don't have this" — G4 finding 2,
 * which made the feature invisible to anyone wanting to withhold) or said nothing useful. Second,
 * two of the three options were the same thought: "I'd rather not store this here" and "I'm
 * providing this another way" both mean *I am not typing this in here*. Showing both real answers
 * at once removes the trigger problem by deletion, and the third option becomes what it always
 * was — a FOLLOW-UP QUESTION about what happens instead.
 *
 * So: two buttons, and `wont_store` opens "Are you providing this another way?" → a free-text
 * location the executor gets to read in the planner's own words.
 *
 * 🔴 THE NOTE IS A LOCATION, NEVER THE SECRET. A planner asked where something is may type the
 * account number, because the question is about the thing. We cannot validate content, so the
 * placeholder models a location and the helper line says so plainly (see decline-copy.ts). This
 * component's job is to make the right answer the obvious one to type.
 *
 * s178 (A3): every string comes from `shared/constants/decline-copy.ts` — this component does not
 * own its words. `density` picks the planner wording for the SURFACE it is mounted on ("compact" =
 * the truncating ribbon cell; "expanded" = drawer / card / PYA).
 *
 * 🔴 The undo is ALWAYS VISIBLE on a declined field (s178) — never hover- or focus-revealed. A
 * state must be leaveable from every surface that can show it: the ribbon once rendered the
 * declined status with no undo anywhere, which is how a field could be declined once and never
 * re-answered (F3).
 *
 * The caller hides/replaces the field input when `declined` is true; this component owns only the
 * buttons / status line. Declining writes `declined=true` + the reason (+ optional note), which
 * counts as RESOLVED for completion — every reason resolves the bar. 🔴 A `wont_store` ALSO stays a
 * counted, named withheld gap, note or no note: the note says where to look first, it does not
 * mean the executor has nothing to do.
 */

import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  plannerUndoLabel,
  plannerDeclineText,
  DECLINE_REASON_OPTION_LABEL,
  DECLINE_NOTE_PROMPT,
  DECLINE_NOTE_LABEL,
  DECLINE_NOTE_PLACEHOLDER,
  DECLINE_NOTE_HELPER,
  DECLINE_NOTE_YES,
  DECLINE_NOTE_NO,
  DECLINE_NOTE_SAVE,
  type DeclineDensity,
} from "@shared/constants/decline-copy";
import { DECLINE_REASONS, DECLINE_NOTE_MAX_LENGTH, type DeclineReason } from "@shared/constants/decline-reason";
import { FONT, SAGE, WARM } from "@shared/brand";


interface FieldDeclineControlProps {
  /** This field's entry is declined. */
  declined: boolean;
  /** 🔴 s179: WHY it was declined. `null`/absent → the conservative `dont_have` wording (the
   *  column is nullable, so every renderer must be total — see decline-copy.ts's FALLBACK note). */
  declineReason?: DeclineReason | null;
  /** 🔴 s179 rebuild: WHERE the executor will find it, in the planner's own words. Only ever
   *  meaningful with `wont_store`. Annotates the gap; never resolves it. */
  declineNote?: string | null;
  /** `declineEligible` on the field: it may be declined at all. */
  eligible: boolean;
  /** The field currently holds a value (a filled field never offers the buttons). */
  hasValue: boolean;
  /** Advisor / executor read-only view: no toggles, but a declined field still reads its status
   *  line so the absence is explicit. */
  readOnly: boolean;
  /** A toggle is mid-flight — disables the buttons to avoid double-fires. */
  pending?: boolean;
  /** 🔴 s179: carries the chosen reason and, for `wont_store`, the optional location note. */
  onDecline: (reason: DeclineReason, note?: string | null) => void;
  onUndo: () => void;
  testId?: string;
  /** s178 (A3): the SURFACE's density, which picks the planner wording (see decline-copy.ts).
   *  Defaults to "expanded" — the drawer/card/PYA case, which is every mount but the ribbon. */
  density?: DeclineDensity;
}

export function FieldDeclineControl({
  declined,
  declineReason,
  declineNote,
  eligible,
  hasValue,
  readOnly,
  pending,
  onDecline,
  onUndo,
  testId,
  density = "expanded",
}: FieldDeclineControlProps) {
  // The follow-up's three states: closed, asking "another way?", collecting the location.
  const [followUp, setFollowUp] = useState<"closed" | "asking" | "note">("closed");
  const [note, setNote] = useState("");

  const closeFollowUp = () => { setFollowUp("closed"); setNote(""); };

  // Value wins (commit 3): a declined field that still holds a value shows the value
  // (the caller renders the input), never the status line.
  if (declined && !hasValue) {
    return (
      <div
        // s155: LEFT-aligned is the product standard. `self-start` stops the control stretching in
        // flex-column parents (drawer / PYA). Set once here so every mount inherits it.
        className="flex flex-col gap-0.5 mt-1 self-start text-left"
        style={{ fontFamily: FONT.sans, fontSize: 13 }}
        data-testid={testId ? `${testId}-declined` : undefined}
        data-decline-reason={declineReason ?? undefined}
      >
        <div className="flex items-center gap-2">
          <span style={{ color: WARM.taupe, fontStyle: "italic" }}>
            {plannerDeclineText(density, declineReason)}
          </span>
          {!readOnly && (
            <button
              type="button"
              disabled={pending}
              onClick={(e) => { e.preventDefault(); onUndo(); }}
              // s178: ALWAYS VISIBLE — no hover/focus reveal. `min-h-6 px-1 -mx-1` keeps the hit
              // area finger-sized in the compact ribbon without moving the text optically.
              className="min-h-6 px-1 -mx-1 underline-offset-2 hover:underline disabled:opacity-50"
              style={{ color: SAGE[600], fontWeight: 500 }}
              aria-label={plannerUndoLabel(declineReason)}
              data-testid={testId ? `${testId}-undo` : undefined}
            >
              {plannerUndoLabel(declineReason)}
            </button>
          )}
        </div>
        {/* The planner's own location note, echoed back so they can see what their executor will
            read. Expanded surfaces only — the ribbon cell has no room for a sentence. */}
        {density === "expanded" && (declineNote ?? "").trim() !== "" && (
          <span style={{ color: WARM.taupe }} data-testid={testId ? `${testId}-note` : undefined}>
            {declineNote}
          </span>
        )}
      </div>
    );
  }

  // Only offer the buttons on an editable, eligible, still-empty field.
  if (!eligible || readOnly || hasValue) return null;

  const pick = (reason: DeclineReason) => {
    // 🔴 `dont_have` is a complete answer on its own — it writes immediately. Only `wont_store`
    // has a follow-up, because only it leaves the executor with somewhere to go.
    if (reason === "dont_have") { onDecline("dont_have"); return; }
    setFollowUp("asking");
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 mt-1 self-start text-left"
      style={{ fontFamily: FONT.sans, fontSize: 13 }}
      data-testid={testId ? `${testId}-options` : undefined}
    >
      {DECLINE_REASONS.map((reason) => (
        <Popover
          key={reason}
          // Only `wont_store` opens anything; `dont_have` writes on click.
          open={reason === "wont_store" && followUp !== "closed"}
          onOpenChange={(o) => { if (!o) closeFollowUp(); }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={pending}
              onClick={(e) => { e.preventDefault(); pick(reason); }}
              className="rounded border px-2 py-1 hover:bg-black/5 disabled:opacity-50"
              style={{ borderColor: WARM.stone, color: WARM.taupe }}
              data-testid={testId ? `${testId}-reason-${reason}` : undefined}
            >
              {DECLINE_REASON_OPTION_LABEL[reason]}
            </button>
          </PopoverTrigger>
          {reason === "wont_store" && (
            <PopoverContent
              align="start"
              className="w-80 p-3"
              style={{ fontFamily: FONT.sans }}
              // The buttons sit in a click-sensitive ribbon row; without this a click inside the
              // panel bubbles to the row and toggles the drawer underneath it.
              onClick={(e) => e.stopPropagation()}
              data-testid={testId ? `${testId}-followup` : undefined}
            >
              {followUp === "asking" ? (
                <>
                  <div className="mb-2" style={{ fontSize: 13, fontWeight: 600, color: WARM.charcoal }}>
                    {DECLINE_NOTE_PROMPT}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setFollowUp("note"); }}
                      className="rounded border px-2 py-1 hover:bg-black/5"
                      style={{ fontSize: 13, borderColor: WARM.stone, color: WARM.charcoal }}
                      data-testid={testId ? `${testId}-followup-yes` : undefined}
                    >
                      {DECLINE_NOTE_YES}
                    </button>
                    <button
                      type="button"
                      // 🔴 "No" is a COMPLETE answer: it writes the decline with no note. The
                      // planner is not obliged to explain themselves to finish answering.
                      onClick={(e) => { e.preventDefault(); closeFollowUp(); onDecline("wont_store", null); }}
                      className="rounded border px-2 py-1 hover:bg-black/5"
                      style={{ fontSize: 13, borderColor: WARM.stone, color: WARM.charcoal }}
                      data-testid={testId ? `${testId}-followup-no` : undefined}
                    >
                      {DECLINE_NOTE_NO}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <label
                    className="mb-1 block"
                    style={{ fontSize: 13, fontWeight: 600, color: WARM.charcoal }}
                    htmlFor={testId ? `${testId}-note-input` : undefined}
                  >
                    {DECLINE_NOTE_LABEL}
                  </label>
                  <input
                    id={testId ? `${testId}-note-input` : undefined}
                    type="text"
                    value={note}
                    maxLength={DECLINE_NOTE_MAX_LENGTH}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={DECLINE_NOTE_PLACEHOLDER}
                    className="w-full rounded border px-2 py-1"
                    style={{ fontSize: 13, borderColor: WARM.stone, color: WARM.charcoal }}
                    data-testid={testId ? `${testId}-note-field` : undefined}
                  />
                  {/* 🔴 The helper is the ONLY thing between this field and a stored secret — we
                      cannot validate content, so it has to be read. Kept directly under the input,
                      where it is read before typing rather than after. */}
                  <p className="mt-1" style={{ fontSize: 11, color: WARM.taupe }} data-testid={testId ? `${testId}-note-helper` : undefined}>
                    {DECLINE_NOTE_HELPER}
                  </p>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={(e) => {
                      e.preventDefault();
                      const v = note.trim();
                      closeFollowUp();
                      onDecline("wont_store", v === "" ? null : v);
                    }}
                    className="mt-2 rounded border px-2 py-1 hover:bg-black/5 disabled:opacity-50"
                    style={{ fontSize: 13, borderColor: WARM.stone, color: SAGE[600], fontWeight: 500 }}
                    data-testid={testId ? `${testId}-note-save` : undefined}
                  >
                    {DECLINE_NOTE_SAVE}
                  </button>
                </>
              )}
            </PopoverContent>
          )}
        </Popover>
      ))}
    </div>
  );
}
